import { createAuditEvent, createEmptyJob, hydrateJob, type IntakeParseResult, IntakeParseResultSchema } from './domain.js';

type Confidence = 'high' | 'medium' | 'low';

const FIELD_DEFINITIONS = [
  { key: 'workOrder', label: 'Work order', confidence: 'high', patterns: [/work\s*order[:\s#-]*(\d{6,12})/i, /\bwo[:\s#-]*(\d{6,12})/i] },
  { key: 'siteId', label: 'Site ID / PSID', confidence: 'high', patterns: [/site\s*id(?:\/psid)?[:\s#-]*([a-z0-9-]+)/i, /psid[:\s#-]*([a-z0-9-]+)/i] },
  { key: 'address', label: 'Address', confidence: 'high', patterns: [/address[:\s-]+([^\n]+)/i, /service\s+address[:\s-]+([^\n]+)/i] },
  { key: 'customerName', label: 'Customer', confidence: 'medium', patterns: [/customer(?:\s+name)?[:\s-]+([^\n]+)/i, /name[:\s-]+([^\n]+)/i] },
  { key: 'phone', label: 'Phone', confidence: 'high', patterns: [/phone[:\s-]+([()\d.\-\s+]+)/i, /tel(?:ephone)?[:\s-]+([()\d.\-\s+]+)/i] },
  { key: 'appointmentType', label: 'Appointment type', confidence: 'medium', patterns: [/appointment\s*type[:\s-]+([^\n]+)/i] },
  { key: 'premiseStatus', label: 'Premise status', confidence: 'medium', patterns: [/premis(?:e)?\s*status[:\s-]+([^\n]+)/i] },
  { key: 'meterNumber', label: 'Meter number', confidence: 'high', patterns: [/meter(?:\s+number)?[:\s#-]+([a-z0-9-]+)/i, /\bmtr[:\s#-]+([a-z0-9-]+)/i] },
  { key: 'meterLocationDescription', label: 'Meter location', confidence: 'medium', patterns: [/meter\s+location(?:\s+desc(?:ription)?)?[:\s-]+([^\n]+)/i] },
  { key: 'prevReading', label: 'Previous reading', confidence: 'high', patterns: [/prev(?:ious)?\s*read(?:ing)?[:\s-]+(\d+)/i] }
] as const;

function cleanValue(value: string): string {
  return value.replace(/\s+/g, ' ').replace(/[|]+/g, ' ').trim();
}

function normalizePhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

function addField(
  result: IntakeParseResult['fields'],
  key: string,
  label: string,
  value: string,
  confidence: Confidence
): void {
  if (!value.trim()) return;
  if (result.some((field) => field.key === key)) return;
  result.push({ key, label, value: cleanValue(value), confidence });
}

export function parseIntakeText(text: string): IntakeParseResult {
  const fields: IntakeParseResult['fields'] = [];
  const normalized = text.replace(/\r\n/g, '\n');

  for (const definition of FIELD_DEFINITIONS) {
    for (const pattern of definition.patterns) {
      const match = normalized.match(pattern);
      if (!match?.[1]) continue;
      const rawValue = definition.key === 'phone' ? normalizePhone(match[1]) : match[1];
      addField(fields, definition.key, definition.label, rawValue, definition.confidence);
      break;
    }
  }

  const draft = createEmptyJob({
    workOrder: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'workOrder')?.value ?? '',
    siteId: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'siteId')?.value ?? '',
    address: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'address')?.value ?? '',
    customerName: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'customerName')?.value ?? '',
    phone: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'phone')?.value ?? '',
    appointmentType: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'appointmentType')?.value ?? '',
    premiseStatus: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'premiseStatus')?.value ?? '',
    meterNumber: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'meterNumber')?.value ?? '',
    meterLocationDescription: fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'meterLocationDescription')?.value ?? '',
    prevReading: Number(fields.find((field: IntakeParseResult['fields'][number]) => field.key === 'prevReading')?.value ?? '') || null
  });

  const warnings: string[] = [];
  if (!draft.workOrder) warnings.push('Work order not detected');
  if (!draft.address) warnings.push('Address not detected');
  if (!draft.customerName) warnings.push('Customer name not detected');

  const high = fields.filter((field: IntakeParseResult['fields'][number]) => field.confidence === 'high').length;
  const overallConfidence: Confidence = high >= 4 ? 'high' : high >= 2 ? 'medium' : 'low';
  const enrichedDraft = hydrateJob({
    ...draft,
    auditTrail: [createAuditEvent('import', 'Created from paste intake', 'system')]
  });

  return IntakeParseResultSchema.parse({
    overallConfidence,
    warnings,
    fields,
    draft: enrichedDraft
  });
}
